import { types, flow, getType, getSnapshot } from 'mobx-state-tree';
import createApolloClient from '../../../core/createApolloClient/createApolloClient.client';
import userListingsQ from './userListingsQ.graphql';
import { getp } from '../../../helpers/getProp';
import saveQL from '../../../components_new/addListing/saveQL';
import { createListingPhotos } from '../../../components_new/addListing/savePhotosQL';
import _ from 'lodash';
import { ManagePublishStatus } from '../../../components_new/addListing/getListingQL';

const client = createApolloClient();

const RulesModel = types.model({
  id: 0,
  description: '',
});

const PhotosModel = types.model({
  id: 0,
  name: '',
});

const IndividualListingModel = types
  .model({
    id: 0,
    title: types.maybeNull(types.string),
    description: types.maybeNull(types.string),
    category: types.maybeNull(types.number),
    category2: types.maybeNull(types.number),
    basePrice: types.maybeNull(types.number),
    city: types.maybeNull(types.string),
    state: types.maybeNull(types.string),
    street: types.maybeNull(types.string),
    zipcode: types.maybeNull(types.string),
    lat: types.maybeNull(types.number),
    lng: types.maybeNull(types.number),
    buildingName: types.maybeNull(types.string),
    rules: types.array(RulesModel),
    photos: types.array(PhotosModel),
    coverPhotoName: types.maybeNull(types.string),
    userId: types.maybeNull(types.string),
    isPublished: types.optional(types.boolean, false),
    error: '',
  })
  .actions(self => {
    let changes = {};
    return {
      save: flow(function*(group) {
        if (Object.keys(changes).length === 0) {
          return;
        }
        const res = yield saveQL(self.buildVars(group));
        self.handleResults(res);
      }),
      saveNew: flow(function*() {
        const vars = _.cloneDeep(self);
        vars.rules = vars.rules.map(r => r.description);

        const res = yield saveQL(vars);
        self.handleResults(res);
        const values = getp(res, 'data.createListing');
        self.id = values.id;
        self.isPublished = values.isPublished;
      }),
      handleResults: function(res) {
        const errors = getp(res, 'data.errors');
        if (errors) {
          self.error = errors.join(' && ');
        }
        const values = getp(res, 'data.createListing');
        if (values && values.lat) {
          self.lat = values.lat;
          self.lng = values.lng;
        }
        changes = {};
      },
      buildVars: function(group) {
        const vars = {};
        if (self.id) {
          vars.id = self.id;
        }

        _.map(changes, (val, key) => {
          vars[key] = val;
        });

        return vars;
      },
      update: function(prop, val) {
        changes[prop] = val;
        self[prop] = val;
      },
      addRule: function(rule) {
        self.rules.push({ description: rule });
        changes.rules = self.rules.map(r => r.description);
      },
      deleteRule: function(desc) {
        self.rules.splice(self.rules.indexOf(desc), 1);
        changes.rules = self.rules.map(r => r.description);
      },
      deletePhoto: function() {},
      savePhoto: flow(function*(fileName, fileType) {
        const vars = { name: fileName, type: fileType, listId: self.id };

        const photo = yield createListingPhotos(vars)
          .then(result => {
            return result;
          })
          .catch(err => console.log('Photos.js - error', err));
        const photoData = getp(photo, 'CreateListingPhotos');
        if (!photoData) {
          console.log('error saving photo', photo);
          return;
        }

        self.photos.push({
          name: photoData.name,
          id: photoData.id,
          order: photoData.order,
        });

        return photo;
      }),
      togglePublish: flow(function*() {
        try {
          const res = yield ManagePublishStatus(
            self.listId,
            self.isPublished ? 'unPublish' : 'publish',
          );
          if (res) {
            self.isPublished = !self.isPublished;
          }
        } catch (err) {
          console.log(
            'Error swapping publish state',
            err,
            'published: ',
            self.isPublished,
          );
        }
      }),
    };
  })
  .views(self => ({
    get address() {
      return `${self.street}`;
    },
    get address2() {
      return `${self.city}, ${self.state} ${self.zipcode}`;
    },
    get address3() {
      return `${self.lat && self.lat.toFixed(2)} // ${self.lng &&
        self.lng.toFixed(2)}`;
    },
    get categoryName() {
      return FILTER_CATEGORIES[self.category];
    },
    get nextStepText() {
      return 'Save';
    },
    get canNext() {
      return true;
    },
    get ruleSummary() {
      return `${self.rules.length} rules`;
    },
    get listId() {
      return self.id;
    },
    get groupsVisited() {
      return {};
    },
  }));

export default types
  .model({
    listings: types.array(IndividualListingModel),
    currentUserId: 0,
    totalPages: 1,
    editing: '',
    editingListingId: types.maybeNull(types.number),
  })
  .actions(self => {
    return {
      start: flow(function*(userId) {
        self.currentUserId = +userId;
        const res = yield client
          .query({
            query: userListingsQ,
            variables: {
              profileId: self.currentUserId,
            },
          })
          .catch(err => console.log('Error getting listings'));
        const rows = getp(res, 'data.userListings.rows');
        const totalPages = getp(res, 'data.userListings.totalPages');

        const rows2 = rows.map(r => {
          return {
            ...r,
            rules: r.listingRules,
            photos: r.listPhotos,
          };
        });

        self.totalPages = totalPages;
        self.listings = rows2;
      }),
      edit: (subject, listingId) => {
        self.editing = subject;
        self.editingListingId = listingId;
      },
      saveAndClose: flow(function*() {
        yield self.editingListing.save(self.editing);
        self.close();
      }),
      close: flow(function*() {
        self.editing = '';
        self.editingListingId = null;
      }),
      duplicate(l) {
        const snapshot = getSnapshot(l);
        const cloneShot = _.cloneDeep(snapshot);
        cloneShot.id = undefined;
        cloneShot.photos = [];
        const clone = getType(l).create(cloneShot);
        console.log('clone', clone);
        self.listings.push(clone);
        clone.saveNew();
      },
    };
  })
  .views(self => ({
    get isLoggedIn() {
      return true;
    },
    get editingListing() {
      return (
        self.editingListingId &&
        self.listings.filter(x => x.id === self.editingListingId)[0]
      );
    },
  }));

export const FILTER_CATEGORIES = {
  89: 'Business Equipment',
  85: 'Electronics',
  91: 'Recreational Vehicles',
  74: 'Clothing',
  77: 'Home and Kitchen',
  93: 'Lawn and Garden',
  84: 'Outdoor Gear',
  88: 'Party & Wedding Equipment',
  92: 'Venues',
  96: 'Music',
  97: 'DVDs',
  76: 'Sporting Equipment',
  86: 'Tools',
  87: 'Toys and Games',
  95: 'Misc',
};



// WEBPACK FOOTER //
// ./src/components/siteadmin/BulkListings/BulkListingsStore.js